/**
 * Vocabulary registry, ARCHITECTURE.md §4.4.
 *
 * The vocabulary is the only place kinds are defined. `Semantics` is a
 * DISCRIMINATED UNION on `domain` (§4.4.2): one typed object per domain, so
 * `KindEditorDialog` renders a typed form instead of a JSON textarea. The
 * per-domain shapes are exactly the §4.4.2 table plus the `domain` tag; the
 * enclosing `KindDef.domain` must agree with `semantics.domain`.
 *
 * Engine primitives a kind may map onto are CLOSED (§4.4.5): region roles,
 * locomotion, transitions, gravity, facing. Users add kinds, not roles.
 */
import { z } from 'zod';
import { Facing, Pt } from './common';
import { ScaleRef, Transition } from './stage';

export const Domain = z.enum([
  'region_kind',
  'anchor_kind',
  'portal_kind',
  'path_kind',
  'surface',
  'action',
  'affordance',
  'tag',
  'emotion',
  'theme',
  'blocked_word',
  'skin_suffix',
  'clip_modifier',
]);
export type Domain = z.infer<typeof Domain>;

export const AgeBandId = z.enum(['3-4', '5-6', '7-8']);
export type AgeBandId = z.infer<typeof AgeBandId>;

// ---------------------------------------------------------------------------
// Closed engine primitives (§4.4.5)
// ---------------------------------------------------------------------------

export const RegionRole = z.enum(['walk', 'fly', 'swim', 'block', 'occlude', 'hide', 'backdrop', 'support']);
export type RegionRole = z.infer<typeof RegionRole>;

export const Locomotion = z.enum(['walk', 'fly', 'swim', 'climb']);
export type Locomotion = z.infer<typeof Locomotion>;

export const Gravity = z.enum(['down', 'up', 'none']);
export type Gravity = z.infer<typeof Gravity>;

// ---------------------------------------------------------------------------
// Semantics per domain (§4.4.2)
// ---------------------------------------------------------------------------

export const RegionKindSemantics = z.strictObject({
  domain: z.literal('region_kind'),
  roles: z.array(RegionRole),
  surface: z.string().optional(),
  requires: z.array(z.enum(['baseline_y', 'elevation'])).optional(),
  scale_ref: ScaleRef.optional(),
  affords: z.array(z.string()).optional(),
  /** Resolves overlaps: obstacle 40 > water 30 > ground 20 > sky 10. */
  z_order: z.number(),
});

export const AnchorKindSemantics = z.strictObject({
  domain: z.literal('anchor_kind'),
  affords: z.array(z.string()),
  surface: z.string().optional(),
  /** Offset from `at` to the default `stand` point, percent. */
  stand_offset: z.tuple([z.number(), z.number()]),
  /**
   * Named points (`seat`, `top`, `side`, …) as offsets from `at`, percent — the fallback when an
   * anchor carries no `points` of its own (addendum §2.6.2; the annotation heuristics read it).
   */
  point_defaults: z.record(z.string(), z.tuple([z.number(), z.number()])).optional(),
  facing: Facing,
  capacity: z.int().min(1).optional(),
  accepts: z.array(z.string()).optional(),
  occluder_expected: z.boolean().optional(),
});

export const PortalKindSemantics = z.strictObject({
  domain: z.literal('portal_kind'),
  transition: Transition,
  needs_anchor: z.boolean(),
});

export const PathKindSemantics = z.strictObject({
  domain: z.literal('path_kind'),
  locomotion: Locomotion,
});

export const SurfaceSemantics = z.strictObject({
  domain: z.literal('surface'),
  rest_point_default: Pt,
  gravity: Gravity,
  /** floor/water/sky = false; table/bed/shelf = true. */
  needs_anchor: z.boolean(),
});

export const ActionSemantics = z.strictObject({
  domain: z.literal('action'),
  /** Fallback chain, e.g. run -> walk -> idle. */
  fallback: z.array(z.string()),
  loop: z.boolean(),
  needs_anchor_affordance: z.string().optional(),
  sit_offset: Pt.optional(),
  /** Percent of stage width per second at scale 1 (walk 12, run 24, toddle 9). */
  speed_pct_s: z.number().positive().optional(),
});

export const AffordanceSemantics = z.strictObject({
  domain: z.literal('affordance'),
  /** Documentation only. */
  derived_from: z.string(),
});

export const TagSemantics = z.strictObject({
  domain: z.literal('tag'),
  group: z.enum(['mood', 'time', 'place', 'weather', 'free']),
});

export const EmotionSemantics = z.strictObject({
  domain: z.literal('emotion'),
  clip: z.string(),
  band_min: AgeBandId,
});

/** The only source of story themes; `schema_gen.py` builds the outline `theme` enum from enabled rows. */
export const ThemeSemantics = z.strictObject({
  domain: z.literal('theme'),
  beats_hint: z.string(),
  band_min: AgeBandId.optional(),
  example_prompt: z.string().optional(),
});

export const BlockedWordSemantics = z.strictObject({
  domain: z.literal('blocked_word'),
  category: z.enum(['violence', 'fear', 'body', 'adult', 'substances', 'slur', 'imitable', 'custom']),
  severity: z.enum(['block', 'warn']),
  match: z.enum(['word', 'stem', 'phrase']),
});

export const SkinSuffixSemantics = z.strictObject({
  domain: z.literal('skin_suffix'),
});

export const ClipModifierSemantics = z.strictObject({
  domain: z.literal('clip_modifier'),
  sets: z.enum(['facing', 'view', 'mirrored', 'deprecated']),
  value: z.union([z.string(), z.boolean()]),
});

/** One typed semantics shape per domain, keyed for the editor's per-domain form. */
export const SemanticsByDomain = {
  region_kind: RegionKindSemantics,
  anchor_kind: AnchorKindSemantics,
  portal_kind: PortalKindSemantics,
  path_kind: PathKindSemantics,
  surface: SurfaceSemantics,
  action: ActionSemantics,
  affordance: AffordanceSemantics,
  tag: TagSemantics,
  emotion: EmotionSemantics,
  theme: ThemeSemantics,
  blocked_word: BlockedWordSemantics,
  skin_suffix: SkinSuffixSemantics,
  clip_modifier: ClipModifierSemantics,
} as const satisfies Record<Domain, z.ZodObject>;

export const Semantics = z.discriminatedUnion('domain', [
  RegionKindSemantics,
  AnchorKindSemantics,
  PortalKindSemantics,
  PathKindSemantics,
  SurfaceSemantics,
  ActionSemantics,
  AffordanceSemantics,
  TagSemantics,
  EmotionSemantics,
  ThemeSemantics,
  BlockedWordSemantics,
  SkinSuffixSemantics,
  ClipModifierSemantics,
]);
export type Semantics = z.infer<typeof Semantics>;

// ---------------------------------------------------------------------------
// Kind definition and registry
// ---------------------------------------------------------------------------

/** Extra per-instance field rendered in the Properties panel. */
export const FieldDef = z.strictObject({
  key: z.string().min(1),
  label: z.string(),
  type: z.enum(['text', 'number', 'boolean', 'select', 'asset_ref']),
  options: z.array(z.strictObject({ value: z.string(), label: z.string() })).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  default: z.unknown().optional(),
});
export type FieldDef = z.infer<typeof FieldDef>;

export const KindOrigin = z.enum(['builtin', 'sidecar', 'ai', 'user']);
export type KindOrigin = z.infer<typeof KindOrigin>;

/** Lowercase snake_case, optionally prefixed with `x:` for user-added kinds (§4.4.4 rule 1). Blocked words are free lowercase text. */
export const KIND_ID_PATTERN = /^(x:)?[a-z0-9][a-z0-9_:\-' ]*$/;

const KindDefShape = z.strictObject({
  /** 'sky' (builtin) | 'x:lava' (user) — user ids MUST start with 'x:'. */
  id: z.string().min(1).regex(KIND_ID_PATTERN, 'Kind ids are lowercase snake_case, user kinds start with x:'),
  domain: Domain,
  /** { en: 'Sky' } */
  label: z.record(z.string(), z.string()),
  /** Shown to humans AND injected into prompts. */
  description: z.string().optional(),
  ai_hint: z.string().optional(),
  /** OKLCH/hex; auto-assigned for x: kinds. */
  color: z.string(),
  /** lucide icon name. */
  icon: z.string().optional(),
  /** Single key, unique per domain. */
  shortcut: z.string().max(1).optional(),
  applies_to: z.array(z.enum(['stage', 'character', 'object', 'story'])),
  semantics: Semantics,
  fields: z.array(FieldDef),
  origin: KindOrigin,
  enabled: z.boolean(),
  deprecated: z.boolean(),
  /** Deprecated -> successor id. */
  alias_of: z.string().optional(),
  sort_order: z.int(),
});

export const KindDef = KindDefShape.refine((k) => k.semantics.domain === k.domain, {
  message: 'semantics.domain must equal the kind domain',
  path: ['semantics', 'domain'],
}).meta({ id: 'KindDef' });
export type KindDef = z.infer<typeof KindDef>;

/** Body of `POST /api/vocab`: a KindDef minus the server-assigned `origin` / `sort_order`. */
export const KindDefCreate = KindDefShape.omit({ origin: true, sort_order: true });
export type KindDefCreate = z.infer<typeof KindDefCreate>;

export const Vocabulary = z
  .strictObject({
    schema: z.literal('story-studio.vocabulary/1'),
    /** settings.vocab_rev, bumped on every mutation. */
    rev: z.int().min(0),
    /** Sorted by sort_order. Partial so `GET /api/vocab?domain=` can return one domain. */
    domains: z.partialRecord(Domain, z.array(KindDef)),
  })
  .meta({
    id: 'Vocabulary',
    title: 'Vocabulary',
    description: 'Kind registry per domain (story-studio.vocabulary/1).',
  });
export type Vocabulary = z.infer<typeof Vocabulary>;
